import React from 'react';
import { UserProfile } from '@clerk/clerk-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/Card';
import { Switch } from './components/ui/Switch';
import { Input } from './components/ui/Input';
import { Button } from './components/ui/Button';
import { Separator } from './components/ui/Separator';
import { Badge } from './components/ui/Badge';
import { useSubscription } from './hooks/useSubscription';
import { FeatureGuard } from './components/FeatureGuard';
import { Crown, Zap, Check, X } from 'lucide-react';

const Section: React.FC<{ title:string; description?:string; children:React.ReactNode }> = ({title,description,children}) => (
  <Card className="w-full max-w-2xl mb-6">
    <CardHeader>
      <CardTitle>{title}</CardTitle>
      {description && <CardDescription>{description}</CardDescription>}
    </CardHeader>
    <CardContent className="space-y-4">
      {children}
    </CardContent>
  </Card>
);

const SubscriptionCard: React.FC = () => {
  const { subscriptionStatus, currentPlan, isLoading } = useSubscription();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!subscriptionStatus || !currentPlan) {
    return (
      <Card className="w-full max-w-2xl mb-6">
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
          <CardDescription>Manage your subscription and billing</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">Loading subscription information...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getPlanIcon = (planName: string) => {
    switch (planName) {
      case 'enterprise': return <Crown className="w-5 h-5 text-yellow-500" />;
      case 'pro': return <Zap className="w-5 h-5 text-blue-500" />;
      default: return null;
    }
  };

  const formatUsage = (current: number, limit: number) => {
    if (limit === -1) return `${current} (unlimited)`;
    return `${current} / ${limit}`;
  };

  return (
    <Card className="w-full max-w-2xl mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Current Plan</CardTitle>
            <CardDescription>Your current subscription and usage</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {getPlanIcon(currentPlan.name)}
            <Badge variant={currentPlan.name === 'free' ? 'outline' : 'default'}>
              {currentPlan.display_name}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Plan Details */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-medium">Plan</span>
            <span>{currentPlan.display_name}</span>
          </div>
          {currentPlan.price_monthly !== undefined && currentPlan.price_monthly > 0 && (
            <div className="flex items-center justify-between">
              <span className="font-medium">Monthly Price</span>
              <span>${currentPlan.price_monthly}/month</span>
            </div>
          )}
          {subscriptionStatus.subscription.current_period_end && (
            <div className="flex items-center justify-between">
              <span className="font-medium">Next Billing</span>
              <span>{new Date(subscriptionStatus.subscription.current_period_end).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        <Separator />

        {/* Usage Metrics */}
        <div className="space-y-3">
          <h4 className="font-medium">Usage This Month</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Reviews Created</span>
              <span>{subscriptionStatus.usage.reviews_created || 0} / Unlimited</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Storage Used</span>
              <span>{subscriptionStatus.usage.storage_used_gb || 0} GB / Unlimited</span>
            </div>
          </div>
        </div>

        <Separator />

        {/* Features */}
        <div className="space-y-3">
          <h4 className="font-medium">Features</h4>
          <div className="space-y-2">
            {subscriptionStatus.features.map((feature) => (
              <div key={feature.feature} className="flex items-center justify-between text-sm">
                <span className="capitalize">{feature.feature.replace(/_/g, ' ')}</span>
                <div className="flex items-center gap-2">
                  {feature.isEnabled ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <X className="w-4 h-4 text-red-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1">
            View Plans
          </Button>
          <Button className="flex-1">
            Manage Billing
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SettingsPage: React.FC = () => {
  const { hasFeature } = useSubscription();

  return (
    <div className="flex flex-col items-center justify-start p-6 w-full overflow-auto space-y-8">
      {/* Account */}
      <Section title="Account" description="Manage your personal information">
        <UserProfile path="/settings" routing="path" />
      </Section>

      {/* Subscription */}
      <SubscriptionCard />

      {/* Notification Preferences */}
      <Section title="Notifications" description="Choose how you'd like to be notified">
        <div className="flex items-center justify-between">
          <span>Email notifications</span>
          <Switch defaultChecked />
        </div>
        <div className="flex items-center justify-between">
          <span>In-app notifications</span>
          <Switch defaultChecked />
        </div>
        <FeatureGuard feature="slack_integration" showUpgradePrompt={false}>
          <div className="flex items-center justify-between">
            <span>Slack notifications</span>
            <Switch />
          </div>
        </FeatureGuard>
      </Section>

      {/* Default Review Settings */}
      <Section title="Default Review Settings" description="These values are pre-filled when you create a new review">
        <label className="flex flex-col space-y-1">
          <span className="text-sm">Default deadline offset (days)</span>
          <Input type="number" placeholder="e.g. 7" />
        </label>
        <div className="flex items-center justify-between">
          <span>Create default reminders</span>
          <Switch />
        </div>
      </Section>

      {/* Branding */}
      <FeatureGuard feature="custom_branding" showUpgradePrompt={false}>
        <Section title="Brand & Appearance" description="Customize how reviewers see your workspace">
          <label className="flex flex-col space-y-1">
            <span className="text-sm">Logo</span>
            <Input type="file" />
          </label>
          <label className="flex flex-col space-y-1">
            <span className="text-sm">Accent color</span>
            <Input type="color" />
          </label>
        </Section>
      </FeatureGuard>

      {/* Team */}
      <Section title="Team" description="Invite your colleagues">
        <div className="text-sm text-muted-foreground">You have 3 of 5 seats used.</div>
        <Button>Invite teammate</Button>
      </Section>

      {/* Analytics */}
      <FeatureGuard feature="advanced_analytics" showUpgradePrompt={false}>
        <Section title="Analytics Settings" description="Configure your analytics preferences">
          <div className="flex items-center justify-between">
            <span>Export analytics data</span>
            <Switch />
          </div>
          <div className="flex items-center justify-between">
            <span>Share analytics with team</span>
            <Switch />
          </div>
        </Section>
      </FeatureGuard>

      {/* API Access */}
      <FeatureGuard feature="api_access" showUpgradePrompt={false}>
        <Section title="API Access" description="Manage your API keys and integrations">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span>API Key</span>
              <Button variant="outline" size="sm">Generate New Key</Button>
            </div>
            <div className="text-sm text-muted-foreground">
              Use this key to access the Approvia API
            </div>
          </div>
        </Section>
      </FeatureGuard>
    </div>
  );
};

export default SettingsPage; 